import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { DisplayDiscussionForMiniScreenProvider } from "../contexts/DisplayDiscussionForMiniScreenContext";
import Navbar from "../components/Navbar";
import ContentSquelette from "../layouts/ContentSquelette";
import OffCanvas from "../components/OffCanvas";
import { CritereFiltreProvider } from "../contexts/CriteresFiltreContext";
import AnnoncesList from "../components/annonces/AnnoncesList";
import AnnonceViewer from "../components/annonces/AnnonceViewer";
import CreateAnnonceModal from "../components/annonces/CreateAnnonceModal";
import { AuthContext } from "../contexts/AuthContext";
import { socket } from "../services/socket";
import { env } from "../datas.config";
import { cacheList, getCachedList } from "../services/messageCache";
import { MdOutlineAddCircle } from "react-icons/md";
import "../assets/css/component-stories.css";

export default function AnnoncePage() {
  const { user } = useContext(AuthContext);
  const [annonces, setAnnonces] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Ref pour accéder aux annonces courantes dans les callbacks socket
  const annoncesRef = useRef(annonces);
  annoncesRef.current = annonces;

  const syncCache = useCallback((list) => {
    cacheList("annonces", list).catch(() => {});
  }, []);

  const fetchAnnonces = useCallback(async () => {
    // 1. Essayer le cache d'abord (TTL 5 min)
    try {
      const cached = await getCachedList("annonces");
      if (cached) {
        setAnnonces(cached);
        return;
      }
    } catch (_) {}

    // 2. Sinon requête serveur
    try {
      const res = await fetch(`${env.domain}/api/auth/annonces`, {
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        const list = data.annonces || [];
        setAnnonces(list);
        syncCache(list);
      }
    } catch (err) {
      console.error("Erreur fetch annonces:", err);
    }
  }, [syncCache]);

  // Charger les annonces au mount et marquer comme vues
  useEffect(() => {
    fetchAnnonces();
    socket.emit("marquer_annonces_vues", user.userId);
  }, [fetchAnnonces, user.userId]);

  // Écouter les events socket
  useEffect(() => {
    const handleNouvelle = (annonce) => {
      setAnnonces((prev) => {
        if (prev.some((a) => a._id === annonce._id)) return prev;
        const updated = [...prev, annonce];
        syncCache(updated);
        return updated;
      });
    };

    const handleSupprimee = ({ annonceId }) => {
      setAnnonces((prev) => {
        const filtered = prev.filter((a) => a._id !== annonceId);
        syncCache(filtered);
        return filtered;
      });
      setActiveIndex((prev) =>
        Math.min(prev, Math.max(0, annoncesRef.current.length - 2))
      );
    };

    socket.on("nouvelle_annonce_broadcast", handleNouvelle);
    socket.on("annonce_supprimee", handleSupprimee);

    return () => {
      socket.off("nouvelle_annonce_broadcast", handleNouvelle);
      socket.off("annonce_supprimee", handleSupprimee);
    };
  }, [syncCache]);

  const handleDelete = async (annonceId) => {
    try {
      const res = await fetch(`${env.domain}/api/auth/annonces/${annonceId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setAnnonces((prev) => {
          const filtered = prev.filter((a) => a._id !== annonceId);
          syncCache(filtered);
          return filtered;
        });
        setActiveIndex((prev) =>
          Math.min(prev, Math.max(0, annoncesRef.current.length - 2))
        );
      }
    } catch (err) {
      console.error("Erreur suppression annonce:", err);
    }
  };

  const handleCreated = (annonce) => {
    setAnnonces((prev) => {
      if (prev.some((a) => a._id === annonce._id)) return prev;
      const updated = [...prev, annonce];
      syncCache(updated);
      return updated;
    });
    setActiveIndex(annoncesRef.current.length);
  };

  const CreateButton = () => (
    <div className="tyn-aside-head-tools">
      <ul className="link-group gap gx-3">
        <li>
          <button className="link" onClick={() => setShowCreateModal(true)}>
            <MdOutlineAddCircle style={{ fontSize: "20px" }} />
            <span>Nouvelle</span>
          </button>
        </li>
      </ul>
    </div>
  );

  return (
    <DisplayDiscussionForMiniScreenProvider>
      <Navbar />
      <ContentSquelette>
        <CritereFiltreProvider>
          <OffCanvas panel="Annonces" headRight={<CreateButton />}>
            <AnnoncesList
              annonces={annonces}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
              onRefresh={fetchAnnonces}
            />
          </OffCanvas>
          <AnnonceViewer
            annonces={annonces}
            activeIndex={activeIndex}
            onSlideChange={setActiveIndex}
            onDelete={handleDelete}
          />
        </CritereFiltreProvider>
      </ContentSquelette>
      <CreateAnnonceModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />
    </DisplayDiscussionForMiniScreenProvider>
  );
}
